import { useState, useEffect, useMemo } from 'react';

import type { ExperienceTrackerAPI } from '@atlassian/experience-tracker/ExperienceTracker';

import type { ContentProps } from '../content/Content';
import { ExperienceNames } from '../experience-tracker/ExperienceNames';
import { EMBEDDED_PARENT_PRODUCTS } from '../page-common/routes';
import type { ViewPageProps } from '../view-page';
import type { useUrl } from './useUrl';

type Props = {
	experienceTracker: ExperienceTrackerAPI;
	hasFooter?: ContentProps['contentConfiguration']['hasFooter'];
	parentProduct: ContentProps['parentProduct'];
	shouldShowFooter?: ViewPageProps['hasFooterLogo'];
	standaloneConfluenceUrl: ReturnType<typeof useUrl>['standaloneConfluenceUrl'];
};

type UseFooterResult = {
	footerUrl: ReturnType<typeof useUrl>['standaloneConfluenceUrl'];
	showFooter: boolean;
};

export const useFooter = ({
	experienceTracker,
	hasFooter,
	shouldShowFooter,
	parentProduct,
	standaloneConfluenceUrl,
}: Props): UseFooterResult => {
	const [footerUrl, setFooterUrl] =
		useState<typeof standaloneConfluenceUrl>(standaloneConfluenceUrl);

	useEffect(() => {
		setFooterUrl(standaloneConfluenceUrl);
	}, [standaloneConfluenceUrl]);

	useEffect(() => {
		const unsubscribe = experienceTracker.subscribe((event) => {
			const { name, action } = event;
			if (name === ExperienceNames.PAGE_NOT_FOUND && action === 'taskStart') {
				setFooterUrl(
					standaloneConfluenceUrl
						? `${new URL(standaloneConfluenceUrl).origin}/wiki/home`
						: standaloneConfluenceUrl,
				);
			}
		});

		return () => unsubscribe();
	}, [experienceTracker, standaloneConfluenceUrl]);

	/**
	 * Currently footer is hidden for the JSM Portal view which passes
	 * shouldShowFooter (externally, showFooterLogo) prop as false and shown for all other views
	 *
	 * JSM portal view will provide "JSM-Portal" as the parent product eventually
	 * However, team is still testing this product type for Atlassian and anonymous users so the "JSM-Portal" parentProduct is being feature-flagged
	 * We need to support these two parent product keys to keep backward compatible for the time being.
	 *
	 * Clean-up task in backlog: https://hello.atlassian.net/browse/CBT-1278 to remove "JSM" from the logic
	 *
	 * EDIT 7/17: showFooterLogo has been renamed internally for clarity and is now aliased as shouldShowFooter when used internally.
	 * External API remains unchanged.
	 */

	const showFooter = useMemo(() => {
		const parentProducts = [
			EMBEDDED_PARENT_PRODUCTS.JSM_PORTAL,
			EMBEDDED_PARENT_PRODUCTS.JSM,
			EMBEDDED_PARENT_PRODUCTS.JSW,
			EMBEDDED_PARENT_PRODUCTS.JWM,
		];

		return !!hasFooter && !(parentProducts.includes(parentProduct) && !shouldShowFooter);
	}, [parentProduct, shouldShowFooter, hasFooter]);

	return { showFooter, footerUrl };
};
